<script setup lang="ts">
import { useVuelidate } from "@vuelidate/core";
import { required, email, helpers } from "@vuelidate/validators";
import { mustBeWebsite } from "~/composables/validators/useCheckWebsite";
const { t } = useI18n();
const toast = useToast();
const url = useRuntimeConfig().public.baseURL

const organizationTypes = ref();
const languages = ref();

// Payment modal state
const showPaymentModal = ref(false);
const WHOP_CHECKOUT_URL = 'https://whop.com/checkout/plan_0SmyxDt1mmYIV';
const UKRAINE_COUNTRY_CODE = 'UA';

const countries = ref([]);
const getCountries = async (text: string) => {
  await $fetch(`${url}/user/country`, {
    params: { text },
    onResponse({ response }) {
      countries.value = response._data;
    },
  });
};

type NewBot = {
  firstName: string;
  lastName: string;
  email: string;
  countryId: { id: number; name: string; code: string } | null | number;
  clientId: number | null;
  name: string | null;
  companyName: string;
  companyId?: number | null;
  companyWebsite: string;
  isBuisnessAccount: boolean;
  organizationTypeId: number | null;
  inquiry: string;
  phone: number | null | string;
  launchDate: Date | null;
  desiredURI: string
  legalName?: string | null
  beneficialOwner?: string | null
};

const newBot = ref<NewBot>({
  firstName: "",
  lastName: "",
  email: "",
  countryId: null,
  clientId: null,
  name: null,
  companyName: "",
  companyId: null,
  companyWebsite: "",
  isBuisnessAccount: false,
  organizationTypeId: null,
  inquiry: "",
  phone: null,
  launchDate: null,
  desiredURI: "",
});

const isFetching = ref(false)

const emit = defineEmits<{
  register: [];
}>();

const rules = {
  firstName: {
    required: helpers.withMessage(() => t("botReg.nameReq"), required),
  },
  lastName: {
    required: helpers.withMessage(() => t("botReg.lastNameReq"), required),
  },
  email: {
    required: helpers.withMessage(() => t("botReg.emailReq"), required),
    email: helpers.withMessage(() => t("botReg.emailValid"), email),
  },
  name: {
    required: helpers.withMessage(() => t("botReg.nameReq"), required),
  },
  countryId: {
    required: helpers.withMessage(() => t("botReg.countryReq"), required),
  },
  companyName: {
    required: helpers.withMessage(() => t("botReg.companyReq"), required),
  },
  companyWebsite: {
    required: helpers.withMessage(() => t("botReg.siteReq"), required),
    mustBeWebsite: helpers.withMessage(() => t('validation.mustBeWebsite'), mustBeWebsite)
  },
  organizationTypeId: {
    required: helpers.withMessage(() => t("botReg.orgTypeReq"), required),
  },
  inquiry: {
    required: helpers.withMessage(() => t("botReg.inquiryReq"), required),
  },
  phone: {
    required: helpers.withMessage(() => t("botReg.phoneReq"), required)
  },
  launchDate: {
    required: helpers.withMessage(() => t("botReg.launchDateReq"), required),
  }
};

const v$ = useVuelidate(rules, newBot);

const resetNewBot = () => {
  newBot.value.firstName = ""
  newBot.value.lastName = ""
  newBot.value.email = ""
  newBot.value.countryId = null
  newBot.value.clientId = null
  newBot.value.name = null
  newBot.value.companyName = ""
  newBot.value.companyId = null
  newBot.value.companyWebsite = ""
  newBot.value.isBuisnessAccount = false
  newBot.value.organizationTypeId = null
  newBot.value.inquiry = ""
  newBot.value.phone = null
  newBot.value.launchDate = null
  newBot.value.desiredURI = ""
}

const isUkrainian = computed(() => {
  return (newBot.value.countryId as any)?.code === UKRAINE_COUNTRY_CODE;
});

const registerBot = async () => {
  const result = await v$.value.$validate();
  if (result) {
    if (typeof newBot.value.phone === "number") {
      newBot.value.phone = newBot.value.phone.toString();
    }
    const toSend = { ...newBot.value, countryId: (newBot.value.countryId as any)?.id }
    isFetching.value = true
    try {
      await $fetch(`${url}/bots/creation_application`, {
        method: "POST",
        body: toSend,
        async onResponse({ response }) {
          if (response.status === 200) {
            toast.add({
              severity: "success",
              summary: t("statuses.success"),
              detail: t("notifications.success.botApplicationCreated"),
              life: 3000,
            });

            // Show payment modal for non-Ukrainian users
            if (!isUkrainian.value) {
              showPaymentModal.value = true;
            }

            setTimeout(() => {
              isFetching.value = false
            }, 1000)
          }
        },
      });
      // Delay reset if payment modal is shown to avoid clearing data it depends on
      if (isUkrainian.value) {
        resetNewBot()
        v$.value.$reset()
      }
    } catch (error) {
      console.error('Failed to register bot:', error);
    } finally {
      isFetching.value = false
    }
  }
};

const payWithWhop = () => {
  window.open(WHOP_CHECKOUT_URL, '_blank', 'noopener,noreferrer');
  showPaymentModal.value = false;
  resetNewBot();
  v$.value.$reset();
};

const payWithSwift = () => {
  window.open('viber://chat?service=26076', '_blank', 'noopener,noreferrer');
  showPaymentModal.value = false;
  resetNewBot();
  v$.value.$reset();
};

const getOrganizationTypes = async () => {
  await $fetch(`${url}/user/organization/types`, {
    onResponse({ response }) {
      organizationTypes.value = response._data;
    },
  });
};
</script>

<template>
  <form class="bot-reg-form flex flex-col gap-4 w-full" @submit.prevent="registerBot">
    <Toast />
    <InputText class="p-inputtext" v-model="newBot.firstName" :placeholder="$t('botReg.firstName')" />
    <small v-for="error in v$.firstName.$errors" class="text-red-500 -mt-4 text-xs">{{ error.$message }}</small>

    <InputText v-model="newBot.lastName" :placeholder="$t('botReg.lastName')" />
    <small v-for="error in v$.lastName.$errors" class="text-red-500 -mt-4 text-xs">{{ error.$message }}</small>

    <InputText v-model="newBot.email" :placeholder="$t('botReg.email')" />
    <small v-for="error in v$.email.$errors" class="text-red-500 -mt-4 text-xs">{{ error.$message }}</small>

    <InputNumber v-model="newBot.phone as number" :placeholder="$t('botReg.phone')" mode="decimal" :use-grouping="false"
      inputmode="decimal" prefix="+" />
    <small v-for="error in v$.phone.$errors" class="text-red-500 -mt-4 text-xs">{{ error.$message }}</small>

    <div>
      <AutoComplete v-model="newBot.countryId" :suggestions="countries" class="w-full" option-label="name"
        @complete="getCountries($event.query)" :placeholder="$t('botReg.country')" force-selection />
      <small v-for="error in v$.countryId.$errors" class="text-red-500 -mt-4 text-xs">{{ error.$message }}</small>
    </div>

    <InputText v-model="newBot.companyName" :placeholder="$t('botReg.company')" />
    <small v-for="error in v$.companyName.$errors" class="text-red-500 -mt-4 text-xs">{{ error.$message }}</small>

    <InputText v-model="newBot.name" :placeholder="$t('botReg.botName')" />
    <small v-for="error in v$.name.$errors" class="text-red-500 -mt-4 text-xs">{{ error.$message }}</small>

    <InputText v-model="newBot.companyWebsite" :placeholder="$t('botReg.site')" />
    <small v-for="error in v$.companyWebsite.$errors" class="text-red-500 -mt-4 text-xs">{{ error.$message }}</small>

    <div class="flex items-center gap-4">
      <InputSwitch v-model="newBot.isBuisnessAccount" />
      <span>{{ $t("botReg.isBusiness") }}</span>
    </div>

    <Dropdown v-model="newBot.organizationTypeId" :options="organizationTypes" option-label="name" class="w-full"
      option-value="id" :placeholder="$t('botReg.orgType')" @click="getOrganizationTypes()" />
    <small v-for="error in v$.organizationTypeId.$errors" class="text-red-500 -mt-4 text-xs">
      {{ error.$message }}
    </small>

    <div class="w-full">
      <Textarea v-model="newBot.inquiry" :placeholder="$t('botReg.inquiry')" class="grow w-full" />
      <p class="text-xs">{{ $t("botReg.inquiryDescr") }}</p>
      <small v-for="error in v$.inquiry.$errors" class="text-red-500 -mt-4 text-xs">{{ error.$message }}</small>
    </div>

    <div class="w-full">
      <Calendar v-model="newBot.launchDate" :placeholder="$t('botReg.botLaunchDate')" class="w-full"
        :min-date="getTomorrowDay()" />
      <small v-for="error in v$.launchDate.$errors" class="text-red-500 -mt-4 text-xs">{{ error.$message }}</small>
      <p class="text-xs mt-1">{{ $t("botReg.launchDateDescr") }}</p>
    </div>

    <div class="w-full">
      <InputText class="w-full" v-model="newBot.desiredURI" :placeholder="$t('botReg.desiredUri')" />
      <p class="text-xs mt-1">{{ $t("botReg.uriDescr") }}</p>
    </div>

    <div class="w-full">
      <InputText v-model="newBot.legalName" :placeholder="$t('botReg.legalName')" />
      <p class="text-xs mt-1">{{ $t("botReg.legalNameDescription") }}</p>
    </div>

    <div class="w-full">
      <InputText v-model="newBot.beneficialOwner" :placeholder="$t('botReg.beneficialOwner')" />
      <p class="text-xs mt-1">{{ $t("botReg.beneficialOwnerDescription") }}</p>
    </div>

    <Button type="submit" :disabled="isFetching" :label="$t('botReg.regBot')" />
  </form>

  <!-- Payment Modal for non-Ukrainian users -->
  <Dialog v-model:visible="showPaymentModal" modal :header="$t('botReg.payment.title')" :style="{ width: '500px' }" :closable="true">
    <div class="payment-modal-content">
      <p class="payment-description">{{ $t('botReg.payment.description') }}</p>

      <div class="payment-amount">
        <span class="amount">390 EUR</span>
        <span class="note">{{ $t('registerBotLanding.pricing.vatNote') }}</span>
      </div>

      <div class="payment-options">
        <button @click="payWithWhop" class="payment-btn whop">
          <span class="pi pi-credit-card"></span>
          <div>
            <strong>{{ $t('botReg.payment.whop.title') }}</strong>
            <small>{{ $t('botReg.payment.whop.description') }}</small>
          </div>
        </button>

        <button @click="payWithSwift" class="payment-btn swift">
          <span class="pi pi-building"></span>
          <div>
            <strong>{{ $t('botReg.payment.swift.title') }}</strong>
            <small>{{ $t('botReg.payment.swift.description') }}</small>
          </div>
        </button>
      </div>

      <p class="payment-note">{{ $t('botReg.payment.note') }}</p>
    </div>
  </Dialog>
</template>

<style>
.bot-reg-form {
  max-width: 600px;
  margin: 0 auto;
}

.bot-reg-form input {
  width: 100%;
}

.payment-modal-content {
  text-align: center;
}

.payment-description {
  color: #6b7280;
  margin-bottom: 1.5rem;
}

.payment-amount {
  background: linear-gradient(135deg, #3070b6, #044068);
  color: white;
  padding: 1.5rem;
  border-radius: 12px;
  margin-bottom: 1.5rem;
}

.payment-amount .amount {
  font-size: 2.5rem;
  font-weight: 700;
  display: block;
}

.payment-amount .note {
  font-size: 0.85rem;
  opacity: 0.8;
}

.payment-options {
  display: flex;
  flex-direction: column;
  gap: 1rem;
  margin-bottom: 1.5rem;
}

.payment-modal-content .payment-btn {
  display: flex !important;
  flex-direction: row !important;
  align-items: center !important;
  gap: 1rem !important;
  padding: 1.25rem !important;
  border-radius: 12px !important;
  border: 2px solid #e5e7eb !important;
  background: white !important;
  cursor: pointer !important;
  transition: all 0.2s !important;
  text-align: left !important;
  width: 100% !important;
}

.payment-modal-content .payment-btn:hover {
  border-color: #3070b6 !important;
  background: #eff6ff !important;
}

.payment-modal-content .payment-btn .pi {
  font-size: 1.5rem !important;
  color: #3070b6 !important;
  flex-shrink: 0 !important;
}

.payment-modal-content .payment-btn > div {
  flex: 1 !important;
  text-align: left !important;
}

.payment-modal-content .payment-btn strong {
  display: block !important;
  color: #1f2937 !important;
  margin-bottom: 0.25rem !important;
  font-size: 1rem !important;
}

.payment-modal-content .payment-btn small {
  display: block !important;
  color: #6b7280 !important;
  font-size: 0.85rem !important;
}

.payment-modal-content .payment-btn.swift:hover {
  border-color: #0284c7 !important;
}

.payment-modal-content .payment-btn.swift .pi {
  color: #0284c7 !important;
}

.payment-note {
  font-size: 0.85rem;
  color: #9ca3af;
  font-style: italic;
}
</style>
